import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { withNgxsWebSocketPlugin } from '@ngxs/websocket-plugin';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { ToastrModule } from 'ngx-toastr';
import { NgxUiLoaderConfig, NgxUiLoaderModule, PB_DIRECTION, SPINNER } from 'ngx-ui-loader';
import { firstValueFrom } from 'rxjs';
import { CoreState } from './core/state/core.state';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { BanksState } from './modules/admin/banks/banks-state/banks.state';
import { BanksService } from './modules/admin/banks/banks.service';
import { CaslPermissionsState } from './modules/admin/casl-permissions/casl-state/casl-permissions.state';
import { ExampleState } from './modules/admin/example/example-state/example.state';
import { ExampleService } from './modules/admin/example/example.service';
import { StakeholdersState } from './modules/admin/stakeholders/stakeholders-state/stakeholders.state';
import { StakeholdersService } from './modules/admin/stakeholders/stakeholders.service';
import { TemplatesState } from './modules/admin/templates/templates-state/templates.state';
import { UsersState } from './modules/admin/users/users-state/users.state';
import { UsersService } from './modules/admin/users/users.service';
import { WarehousesState } from './modules/admin/warehouses/warehouses-state/warehouses.state';
import { WarehousesService } from './modules/admin/warehouses/warehouses.service';
import { AuditState } from './modules/audit/audit-state/audit.state';
import { AuditService } from './modules/audit/audit.service';
import { AuthState } from './modules/auth/state/auth.state';
import { DashboardState } from './modules/dashboard/dashboard-state/dashboard.state';
import { JournalsState } from './modules/journals/journals-state/journals.state';
import { LeadsState } from './modules/leads/leads-state/leads.state';
import { LeadsService } from './modules/leads/leads.service';
import { NoteState } from './modules/notes/note-state/note.state';
import { NotesService } from './modules/notes/notes.service';
import { OffersState } from './modules/offers/offers-state/offers.state';
import { PurchaseConfirmationsState } from './modules/purchase-confirmations/purchase-confirmations-state/purchase-confirmations.state';
import { PurchaseShippingInstructionsState } from './modules/purchase-shipping-instructions/purchase-shipping-instructions-state/purchase-shipping-instructions-state';
import { PurchaseShippingInstructionsService } from './modules/purchase-shipping-instructions/purchase-shipping-instructions.service';
import { SalesConfirmationsState } from './modules/sales-confirmations/sales-confirmations-state/sales-confirmations.state';
import { SalesInvoiceState } from './modules/sales-invoices/sales-invoices-state/sales-invoices.state';
import { SalesInvoicesService } from './modules/sales-invoices/sales-invoices.service';
import { SalesShippingInstructionsState } from './modules/sales-shipping-instructions/sales-shipping-instructions-state/sales-shipping-instructions.state';
import { SharedState } from './modules/shared-state/shared.state';
import { SharedGridService } from './modules/shared/ag-grid/shared-grid-state/shared-grid.service';
import { SharedGridState } from './modules/shared/ag-grid/shared-grid-state/shared-grid.state';
import { FileManagerState } from './modules/shared/file-manager/file-manager-state/file-manager.state';
import { SharedService } from './modules/shared/shared.service';
import { StockState } from './modules/stock/stock-state/stock.state';
import { StockService } from './modules/stock/stock.service';
import { DropdownState } from './modules/system-setup/dropdown-options/dropdown-state/dropdown.state';

const ngxToastr: ToastrModule = {
    timeOut: 4500,
    enableHtml: true,
};

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
    blur: 8,
    delay: 0,
    fgsColor: '#1E96F7',
    overlayColor: 'rgba(40, 40, 40, 0.5)',
    fgsType: SPINNER.threeBounce,
    pbDirection: PB_DIRECTION.leftToRight, // progress bar direction
    pbThickness: 5, // progress bar thickness
    pbColor: '#1E96F7',
    logoUrl: 'logos/logo.png',
    logoSize: 100,
    textColor: '#808080',
    maxTime: 999999, // max time set to 30 seconds
    minTime: 500    // min time set to 5 seconds
};

export const appConfig: ApplicationConfig = {
    providers: [
        SharedService,
        ExampleService,
        SharedGridService,
        NotesService,
        StakeholdersService,
        BanksService,
        WarehousesService,
        UsersService,
        AuditService,
        PurchaseShippingInstructionsService,
        StockService,
        LeadsService,
        SalesInvoicesService,
        importProvidersFrom(
            ToastrModule.forRoot(ngxToastr),
            NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        ),
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
            useValue: {
                useUtc: true,
            },
        },
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const initializerFn = (() => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            })();
            return initializerFn();
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'compact',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        provideStore(
            [CoreState,
                AuthState,
                SharedState,
                SharedGridState,
                ExampleState,
                NoteState,
                FileManagerState,
                StakeholdersState,
                BanksState,
                WarehousesState,
                UsersState,
                CaslPermissionsState,
                DropdownState,
                AuditState,
                PurchaseConfirmationsState,
                PurchaseShippingInstructionsState,
                TemplatesState,
                PurchaseConfirmationsState,
                OffersState,
                SalesConfirmationsState,
                StockState,
                LeadsState,
                SalesShippingInstructionsState,
                SalesInvoiceState,
                DashboardState,
                JournalsState,
            ],
            withNgxsReduxDevtoolsPlugin(),
            // withNgxsLoggerPlugin(),
            withNgxsStoragePlugin({ keys: ['!authState', '!coreState'] }),
            withNgxsFormPlugin(),
            withNgxsRouterPlugin(),
            withNgxsWebSocketPlugin())
    ],
};
